/**
 * Binance Derivatives Trading Portfolio Margin Pro REST API
 *
 * OpenAPI Specification for the Binance Derivatives Trading Portfolio Margin Pro REST API
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {
    ConfigurationRestAPI,
    TimeUnit,
    RestApiResponse,
    assertParamExists,
    sendRequest,
    type RequestArgs,
} from '@binance/common';
import type {
    BnbTransferResponse,
    ChangeAutoRepayFuturesStatusResponse,
    DeleteMarginCallLevelResponse,
    FundAutoCollectionResponse,
    FundCollectionByAssetResponse,
    GetAutoRepayFuturesStatusResponse,
    GetDeltaModeStatusResponse,
    GetMarginCallLevelResponse,
    GetPortfolioMarginProAccountBalanceResponse,
    GetPortfolioMarginProAccountInfoResponse,
    GetPortfolioMarginProSpanAccountInfoResponse,
    GetTransferableEarnAssetBalanceForPortfolioMarginResponse,
    PortfolioMarginProBankruptcyLoanRepayResponse,
    QueryPortfolioMarginProBankruptcyLoanAmountResponse,
    QueryPortfolioMarginProBankruptcyLoanRepayHistoryResponse,
    QueryPortfolioMarginProNegativeBalanceInterestHistoryResponse,
    RepayFuturesNegativeBalanceResponse,
    SetMarginCallLevelResponse,
    SwitchDeltaModeResponse,
    TransferLdusdtRwusdForPortfolioMarginResponse,
} from '../types';

/**
 * AccountApi - axios parameter creator
 */
const AccountApiAxiosParamCreator = function (configuration: ConfigurationRestAPI) {
    return {
        /**
         * BNB transfer can be between Margin Account and USDM Account
         *
         *
         * You can only use this function 2 times per 10 minutes in a rolling manner
         *
         * Weight: 1500
         *
         * @summary BNB transfer(USER_DATA)
         * @param {number} amount
         * @param {string} transferSide "TO_UM","FROM_UM"
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        bnbTransfer: async (
            amount: number,
            transferSide: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'amount' is not null or undefined
            assertParamExists('bnbTransfer', 'amount', amount);
            // verify required parameter 'transferSide' is not null or undefined
            assertParamExists('bnbTransfer', 'transferSide', transferSide);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (amount !== undefined && amount !== null) {
                localVarQueryParameter['amount'] = amount;
            }
            if (transferSide !== undefined && transferSide !== null) {
                localVarQueryParameter['transferSide'] = transferSide;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/bnb-transfer',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Change Auto-repay-futures Status
         *
         * Weight: 1500
         *
         * @summary Change Auto-repay-futures Status(TRADE)
         * @param {string} autoRepay Default: `true`; `false` for turn off the auto-repay futures negative balance function
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        changeAutoRepayFuturesStatus: async (
            autoRepay: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'autoRepay' is not null or undefined
            assertParamExists('changeAutoRepayFuturesStatus', 'autoRepay', autoRepay);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (autoRepay !== undefined && autoRepay !== null) {
                localVarQueryParameter['autoRepay'] = autoRepay;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/repay-futures-switch',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Delete the margin call level for a Portfolio Margin account.
         *
         * Weight: 1500
         *
         * @summary Delete Margin Call Level (USER_DATA)
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        deleteMarginCallLevel: async (recvWindow?: number | bigint): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/margin-call-level',
                method: 'DELETE',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Transfers all assets from Futures Account to Margin account
         *
         * The BNB would not be collected from UM-PM account to the Portfolio Margin account.
         * You can only use this function 500 times per hour in a rolling manner.
         *
         * Weight: 1500
         *
         * @summary Fund Auto-collection(USER_DATA)
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        fundAutoCollection: async (recvWindow?: number | bigint): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/auto-collection',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Transfers specific asset from Futures Account to Margin account
         *
         * The BNB transfer is not be supported
         *
         * Weight: 60
         *
         * @summary Fund Collection by Asset(USER_DATA)
         * @param {string} asset `LDUSDT` and `RWUSD`
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        fundCollectionByAsset: async (
            asset: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'asset' is not null or undefined
            assertParamExists('fundCollectionByAsset', 'asset', asset);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (asset !== undefined && asset !== null) {
                localVarQueryParameter['asset'] = asset;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/asset-collection',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query Auto-repay-futures Status
         *
         * Weight: 30
         *
         * @summary Get Auto-repay-futures Status(USER_DATA)
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getAutoRepayFuturesStatus: async (recvWindow?: number | bigint): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/repay-futures-switch',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query the Delta mode status of current account.
         *
         * Weight: 1500
         *
         * @summary Get Delta Mode Status(USER_DATA)
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getDeltaModeStatus: async (recvWindow?: number | bigint): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/delta-mode',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get the margin call level for a Portfolio Margin account.
         *
         * Weight: 1500
         *
         * @summary Get Margin Call Level (USER_DATA)
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getMarginCallLevel: async (recvWindow?: number | bigint): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/margin-call-level',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query Portfolio Margin Pro account balance
         *
         * Weight: 20
         *
         * @summary Get Portfolio Margin Pro Account Balance(USER_DATA)
         * @param {string} [asset]
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getPortfolioMarginProAccountBalance: async (
            asset?: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (asset !== undefined && asset !== null) {
                localVarQueryParameter['asset'] = asset;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/balance',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get Portfolio Margin Pro Account Info
         *
         * Weight: 5
         *
         * @summary Get Portfolio Margin Pro Account Info(USER_DATA)
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getPortfolioMarginProAccountInfo: async (
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/account',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get Portfolio Margin Pro SPAN Account Info (For Portfolio Margin Pro SPAN users only)
         *
         * Weight: 5
         *
         * @summary Get Portfolio Margin Pro SPAN Account Info(USER_DATA)
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getPortfolioMarginProSpanAccountInfo: async (
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v2/portfolio/account',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Get transferable earn asset balance for all types of Portfolio Margin account
         *
         * Weight: 1500
         *
         * @summary Get Transferable Earn Asset Balance for Portfolio Margin (USER_DATA)
         * @param {string} asset `LDUSDT` and `RWUSD`
         * @param {string} transferType `EARN_TO_FUTURE` /`FUTURE_TO_EARN`
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        getTransferableEarnAssetBalanceForPortfolioMargin: async (
            asset: string,
            transferType: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'asset' is not null or undefined
            assertParamExists('getTransferableEarnAssetBalanceForPortfolioMargin', 'asset', asset);
            // verify required parameter 'transferType' is not null or undefined
            assertParamExists(
                'getTransferableEarnAssetBalanceForPortfolioMargin',
                'transferType',
                transferType
            );

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (asset !== undefined && asset !== null) {
                localVarQueryParameter['asset'] = asset;
            }
            if (transferType !== undefined && transferType !== null) {
                localVarQueryParameter['transferType'] = transferType;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/earn-asset-balance',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Repay Portfolio Margin Pro Bankruptcy Loan
         *
         * Please note that the API Key has enabled Spot & Margin Trading permissions to access this endpoint.
         *
         * Weight: 3000
         *
         * @summary Portfolio Margin Pro Bankruptcy Loan Repay
         * @param {string} [from] SPOT or MARGIN，default SPOT
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        portfolioMarginProBankruptcyLoanRepay: async (
            from?: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (from !== undefined && from !== null) {
                localVarQueryParameter['from'] = from;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/repay',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query Portfolio Margin Pro Bankruptcy Loan Amount
         *
         * If there’s no classic portfolio margin bankruptcy loan, the amount would be 0
         *
         * Weight: 500
         *
         * @summary Query Portfolio Margin Pro Bankruptcy Loan Amount(USER_DATA)
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        queryPortfolioMarginProBankruptcyLoanAmount: async (
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/pmLoan',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query repay history of pmloan for portfolio margin pro.
         *
         * `startTime` and `endTime` cannot be longer than 360 days
         * If `startTime` and `endTime` not sent, return records of the last 30 days by default.
         * If `startTime`is sent and `endTime` is not sent, return records of [startTime, startTime+30d].
         * If `startTime` is not sent and `endTime` is sent, return records of [endTime-30d, endTime].
         *
         * Weight: 500
         *
         * @summary Query Portfolio Margin Pro Bankruptcy Loan Repay History(USER_DATA)
         * @param {number | bigint} [startTime]
         * @param {number | bigint} [endTime]
         * @param {number | bigint} [current] Currently querying page. Start from 1. Default:1
         * @param {number | bigint} [size] Default:10 Max:100
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        queryPortfolioMarginProBankruptcyLoanRepayHistory: async (
            startTime?: number | bigint,
            endTime?: number | bigint,
            current?: number | bigint,
            size?: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (current !== undefined && current !== null) {
                localVarQueryParameter['current'] = current;
            }
            if (size !== undefined && size !== null) {
                localVarQueryParameter['size'] = size;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/pmloan-history',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Query interest history of negative balance for portfolio margin.
         *
         * Weight: 50
         *
         * @summary Query Portfolio Margin Pro Negative Balance Interest History(USER_DATA)
         * @param {string} [asset]
         * @param {number | bigint} [startTime]
         * @param {number | bigint} [endTime]
         * @param {number | bigint} [size] Default:10 Max:100
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        queryPortfolioMarginProNegativeBalanceInterestHistory: async (
            asset?: string,
            startTime?: number | bigint,
            endTime?: number | bigint,
            size?: number | bigint,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (asset !== undefined && asset !== null) {
                localVarQueryParameter['asset'] = asset;
            }
            if (startTime !== undefined && startTime !== null) {
                localVarQueryParameter['startTime'] = startTime;
            }
            if (endTime !== undefined && endTime !== null) {
                localVarQueryParameter['endTime'] = endTime;
            }
            if (size !== undefined && size !== null) {
                localVarQueryParameter['size'] = size;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/interest-history',
                method: 'GET',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Repay futures Negative Balance
         *
         * Weight: 1500
         *
         * @summary Repay futures Negative Balance(USER_DATA)
         * @param {string} [from] SPOT or MARGIN，default SPOT
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        repayFuturesNegativeBalance: async (
            from?: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (from !== undefined && from !== null) {
                localVarQueryParameter['from'] = from;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/repay-futures-negative-balance',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Set the margin call level for a Portfolio Margin account. When the account's uniMMR drops to the specified level, a notification will be sent via email and SMS.
         *
         * Weight: 1500
         *
         * @summary Set Margin Call Level (USER_DATA)
         * @param {number} marginCallLevel The value of marginCallLevel must be within the range [1.1, 2.0].
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        setMarginCallLevel: async (
            marginCallLevel: number,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'marginCallLevel' is not null or undefined
            assertParamExists('setMarginCallLevel', 'marginCallLevel', marginCallLevel);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (marginCallLevel !== undefined && marginCallLevel !== null) {
                localVarQueryParameter['marginCallLevel'] = marginCallLevel;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/margin-call-level',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Switch the Delta mode for existing PM PRO / PM RETAIL accounts.
         *
         * Weight: 1500
         *
         * @summary Switch Delta Mode(TRADE)
         * @param {string} deltaEnabled `true` to enable Delta mode; `false` to disable Delta mode
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        switchDeltaMode: async (
            deltaEnabled: string,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'deltaEnabled' is not null or undefined
            assertParamExists('switchDeltaMode', 'deltaEnabled', deltaEnabled);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (deltaEnabled !== undefined && deltaEnabled !== null) {
                localVarQueryParameter['deltaEnabled'] = deltaEnabled;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/delta-mode',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
        /**
         * Transfer LDUSDT/RWUSD as collateral for all types of Portfolio Margin account
         *
         * Weight: 1500
         *
         * @summary Transfer LDUSDT/RWUSD for Portfolio Margin(TRADE)
         * @param {string} asset `LDUSDT` and `RWUSD`
         * @param {string} transferType `EARN_TO_FUTURE` /`FUTURE_TO_EARN`
         * @param {number} amount
         * @param {number | bigint} [recvWindow]
         *
         * @throws {RequiredError}
         */
        transferLdusdtRwusdForPortfolioMargin: async (
            asset: string,
            transferType: string,
            amount: number,
            recvWindow?: number | bigint
        ): Promise<RequestArgs> => {
            // verify required parameter 'asset' is not null or undefined
            assertParamExists('transferLdusdtRwusdForPortfolioMargin', 'asset', asset);
            // verify required parameter 'transferType' is not null or undefined
            assertParamExists(
                'transferLdusdtRwusdForPortfolioMargin',
                'transferType',
                transferType
            );
            // verify required parameter 'amount' is not null or undefined
            assertParamExists('transferLdusdtRwusdForPortfolioMargin', 'amount', amount);

            const localVarQueryParameter: Record<string, unknown> = {};
            const localVarBodyParameter: Record<string, unknown> = {};
            const localVarHeaderParameter: Record<string, unknown> = {};

            if (asset !== undefined && asset !== null) {
                localVarQueryParameter['asset'] = asset;
            }
            if (transferType !== undefined && transferType !== null) {
                localVarQueryParameter['transferType'] = transferType;
            }
            if (amount !== undefined && amount !== null) {
                localVarQueryParameter['amount'] = amount;
            }
            if (recvWindow !== undefined && recvWindow !== null) {
                localVarQueryParameter['recvWindow'] = recvWindow;
            }

            let _timeUnit: TimeUnit | undefined;
            if ('timeUnit' in configuration) _timeUnit = configuration.timeUnit as TimeUnit;

            return {
                endpoint: '/sapi/v1/portfolio/earn-asset-transfer',
                method: 'POST',
                queryParams: localVarQueryParameter,
                bodyParams: localVarBodyParameter,
                headerParams: localVarHeaderParameter,
                timeUnit: _timeUnit,
            };
        },
    };
};

/**
 * AccountApi - interface
 * @interface AccountApi
 */
export interface AccountApiInterface {
    /**
     * BNB transfer can be between Margin Account and USDM Account
     *
     *
     * You can only use this function 2 times per 10 minutes in a rolling manner
     *
     * Weight: 1500
     *
     * @summary BNB transfer(USER_DATA)
     * @param {BnbTransferRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    bnbTransfer(
        requestParameters: BnbTransferRequest
    ): Promise<RestApiResponse<BnbTransferResponse>>;
    /**
     * Change Auto-repay-futures Status
     *
     * Weight: 1500
     *
     * @summary Change Auto-repay-futures Status(TRADE)
     * @param {ChangeAutoRepayFuturesStatusRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    changeAutoRepayFuturesStatus(
        requestParameters: ChangeAutoRepayFuturesStatusRequest
    ): Promise<RestApiResponse<ChangeAutoRepayFuturesStatusResponse>>;
    /**
     * Delete the margin call level for a Portfolio Margin account.
     *
     * Weight: 1500
     *
     * @summary Delete Margin Call Level (USER_DATA)
     * @param {DeleteMarginCallLevelRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    deleteMarginCallLevel(
        requestParameters?: DeleteMarginCallLevelRequest
    ): Promise<RestApiResponse<DeleteMarginCallLevelResponse>>;
    /**
     * Transfers all assets from Futures Account to Margin account
     *
     * The BNB would not be collected from UM-PM account to the Portfolio Margin account.
     * You can only use this function 500 times per hour in a rolling manner.
     *
     * Weight: 1500
     *
     * @summary Fund Auto-collection(USER_DATA)
     * @param {FundAutoCollectionRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    fundAutoCollection(
        requestParameters?: FundAutoCollectionRequest
    ): Promise<RestApiResponse<FundAutoCollectionResponse>>;
    /**
     * Transfers specific asset from Futures Account to Margin account
     *
     * The BNB transfer is not be supported
     *
     * Weight: 60
     *
     * @summary Fund Collection by Asset(USER_DATA)
     * @param {FundCollectionByAssetRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    fundCollectionByAsset(
        requestParameters: FundCollectionByAssetRequest
    ): Promise<RestApiResponse<FundCollectionByAssetResponse>>;
    /**
     * Query Auto-repay-futures Status
     *
     * Weight: 30
     *
     * @summary Get Auto-repay-futures Status(USER_DATA)
     * @param {GetAutoRepayFuturesStatusRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getAutoRepayFuturesStatus(
        requestParameters?: GetAutoRepayFuturesStatusRequest
    ): Promise<RestApiResponse<GetAutoRepayFuturesStatusResponse>>;
    /**
     * Query the Delta mode status of current account.
     *
     * Weight: 1500
     *
     * @summary Get Delta Mode Status(USER_DATA)
     * @param {GetDeltaModeStatusRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getDeltaModeStatus(
        requestParameters?: GetDeltaModeStatusRequest
    ): Promise<RestApiResponse<GetDeltaModeStatusResponse>>;
    /**
     * Get the margin call level for a Portfolio Margin account.
     *
     * Weight: 1500
     *
     * @summary Get Margin Call Level (USER_DATA)
     * @param {GetMarginCallLevelRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getMarginCallLevel(
        requestParameters?: GetMarginCallLevelRequest
    ): Promise<RestApiResponse<GetMarginCallLevelResponse>>;
    /**
     * Query Portfolio Margin Pro account balance
     *
     * Weight: 20
     *
     * @summary Get Portfolio Margin Pro Account Balance(USER_DATA)
     * @param {GetPortfolioMarginProAccountBalanceRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getPortfolioMarginProAccountBalance(
        requestParameters?: GetPortfolioMarginProAccountBalanceRequest
    ): Promise<RestApiResponse<GetPortfolioMarginProAccountBalanceResponse>>;
    /**
     * Get Portfolio Margin Pro Account Info
     *
     * Weight: 5
     *
     * @summary Get Portfolio Margin Pro Account Info(USER_DATA)
     * @param {GetPortfolioMarginProAccountInfoRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getPortfolioMarginProAccountInfo(
        requestParameters?: GetPortfolioMarginProAccountInfoRequest
    ): Promise<RestApiResponse<GetPortfolioMarginProAccountInfoResponse>>;
    /**
     * Get Portfolio Margin Pro SPAN Account Info (For Portfolio Margin Pro SPAN users only)
     *
     * Weight: 5
     *
     * @summary Get Portfolio Margin Pro SPAN Account Info(USER_DATA)
     * @param {GetPortfolioMarginProSpanAccountInfoRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getPortfolioMarginProSpanAccountInfo(
        requestParameters?: GetPortfolioMarginProSpanAccountInfoRequest
    ): Promise<RestApiResponse<GetPortfolioMarginProSpanAccountInfoResponse>>;
    /**
     * Get transferable earn asset balance for all types of Portfolio Margin account
     *
     * Weight: 1500
     *
     * @summary Get Transferable Earn Asset Balance for Portfolio Margin (USER_DATA)
     * @param {GetTransferableEarnAssetBalanceForPortfolioMarginRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    getTransferableEarnAssetBalanceForPortfolioMargin(
        requestParameters: GetTransferableEarnAssetBalanceForPortfolioMarginRequest
    ): Promise<RestApiResponse<GetTransferableEarnAssetBalanceForPortfolioMarginResponse>>;
    /**
     * Repay Portfolio Margin Pro Bankruptcy Loan
     *
     * Please note that the API Key has enabled Spot & Margin Trading permissions to access this endpoint.
     *
     * Weight: 3000
     *
     * @summary Portfolio Margin Pro Bankruptcy Loan Repay
     * @param {PortfolioMarginProBankruptcyLoanRepayRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    portfolioMarginProBankruptcyLoanRepay(
        requestParameters?: PortfolioMarginProBankruptcyLoanRepayRequest
    ): Promise<RestApiResponse<PortfolioMarginProBankruptcyLoanRepayResponse>>;
    /**
     * Query Portfolio Margin Pro Bankruptcy Loan Amount
     *
     * If there’s no classic portfolio margin bankruptcy loan, the amount would be 0
     *
     * Weight: 500
     *
     * @summary Query Portfolio Margin Pro Bankruptcy Loan Amount(USER_DATA)
     * @param {QueryPortfolioMarginProBankruptcyLoanAmountRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    queryPortfolioMarginProBankruptcyLoanAmount(
        requestParameters?: QueryPortfolioMarginProBankruptcyLoanAmountRequest
    ): Promise<RestApiResponse<QueryPortfolioMarginProBankruptcyLoanAmountResponse>>;
    /**
     * Query repay history of pmloan for portfolio margin pro.
     *
     * `startTime` and `endTime` cannot be longer than 360 days
     * If `startTime` and `endTime` not sent, return records of the last 30 days by default.
     * If `startTime`is sent and `endTime` is not sent, return records of [startTime, startTime+30d].
     * If `startTime` is not sent and `endTime` is sent, return records of [endTime-30d, endTime].
     *
     * Weight: 500
     *
     * @summary Query Portfolio Margin Pro Bankruptcy Loan Repay History(USER_DATA)
     * @param {QueryPortfolioMarginProBankruptcyLoanRepayHistoryRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    queryPortfolioMarginProBankruptcyLoanRepayHistory(
        requestParameters?: QueryPortfolioMarginProBankruptcyLoanRepayHistoryRequest
    ): Promise<RestApiResponse<QueryPortfolioMarginProBankruptcyLoanRepayHistoryResponse>>;
    /**
     * Query interest history of negative balance for portfolio margin.
     *
     * Weight: 50
     *
     * @summary Query Portfolio Margin Pro Negative Balance Interest History(USER_DATA)
     * @param {QueryPortfolioMarginProNegativeBalanceInterestHistoryRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    queryPortfolioMarginProNegativeBalanceInterestHistory(
        requestParameters?: QueryPortfolioMarginProNegativeBalanceInterestHistoryRequest
    ): Promise<RestApiResponse<QueryPortfolioMarginProNegativeBalanceInterestHistoryResponse>>;
    /**
     * Repay futures Negative Balance
     *
     * Weight: 1500
     *
     * @summary Repay futures Negative Balance(USER_DATA)
     * @param {RepayFuturesNegativeBalanceRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    repayFuturesNegativeBalance(
        requestParameters?: RepayFuturesNegativeBalanceRequest
    ): Promise<RestApiResponse<RepayFuturesNegativeBalanceResponse>>;
    /**
     * Set the margin call level for a Portfolio Margin account. When the account's uniMMR drops to the specified level, a notification will be sent via email and SMS.
     *
     * Weight: 1500
     *
     * @summary Set Margin Call Level (USER_DATA)
     * @param {SetMarginCallLevelRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    setMarginCallLevel(
        requestParameters: SetMarginCallLevelRequest
    ): Promise<RestApiResponse<SetMarginCallLevelResponse>>;
    /**
     * Switch the Delta mode for existing PM PRO / PM RETAIL accounts.
     *
     * Weight: 1500
     *
     * @summary Switch Delta Mode(TRADE)
     * @param {SwitchDeltaModeRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    switchDeltaMode(
        requestParameters: SwitchDeltaModeRequest
    ): Promise<RestApiResponse<SwitchDeltaModeResponse>>;
    /**
     * Transfer LDUSDT/RWUSD as collateral for all types of Portfolio Margin account
     *
     * Weight: 1500
     *
     * @summary Transfer LDUSDT/RWUSD for Portfolio Margin(TRADE)
     * @param {TransferLdusdtRwusdForPortfolioMarginRequest} requestParameters Request parameters.
     *
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApiInterface
     */
    transferLdusdtRwusdForPortfolioMargin(
        requestParameters: TransferLdusdtRwusdForPortfolioMarginRequest
    ): Promise<RestApiResponse<TransferLdusdtRwusdForPortfolioMarginResponse>>;
}

/**
 * Request parameters for bnbTransfer operation in AccountApi.
 * @interface BnbTransferRequest
 */
export interface BnbTransferRequest {
    /**
     *
     * @type {number}
     * @memberof AccountApiBnbTransfer
     */
    readonly amount: number;

    /**
     * "TO_UM","FROM_UM"
     * @type {string}
     * @memberof AccountApiBnbTransfer
     */
    readonly transferSide: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiBnbTransfer
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for changeAutoRepayFuturesStatus operation in AccountApi.
 * @interface ChangeAutoRepayFuturesStatusRequest
 */
export interface ChangeAutoRepayFuturesStatusRequest {
    /**
     * Default: `true`; `false` for turn off the auto-repay futures negative balance function
     * @type {string}
     * @memberof AccountApiChangeAutoRepayFuturesStatus
     */
    readonly autoRepay: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiChangeAutoRepayFuturesStatus
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for deleteMarginCallLevel operation in AccountApi.
 * @interface DeleteMarginCallLevelRequest
 */
export interface DeleteMarginCallLevelRequest {
    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiDeleteMarginCallLevel
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for fundAutoCollection operation in AccountApi.
 * @interface FundAutoCollectionRequest
 */
export interface FundAutoCollectionRequest {
    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiFundAutoCollection
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for fundCollectionByAsset operation in AccountApi.
 * @interface FundCollectionByAssetRequest
 */
export interface FundCollectionByAssetRequest {
    /**
     * `LDUSDT` and `RWUSD`
     * @type {string}
     * @memberof AccountApiFundCollectionByAsset
     */
    readonly asset: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiFundCollectionByAsset
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getAutoRepayFuturesStatus operation in AccountApi.
 * @interface GetAutoRepayFuturesStatusRequest
 */
export interface GetAutoRepayFuturesStatusRequest {
    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetAutoRepayFuturesStatus
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getDeltaModeStatus operation in AccountApi.
 * @interface GetDeltaModeStatusRequest
 */
export interface GetDeltaModeStatusRequest {
    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetDeltaModeStatus
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getMarginCallLevel operation in AccountApi.
 * @interface GetMarginCallLevelRequest
 */
export interface GetMarginCallLevelRequest {
    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetMarginCallLevel
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getPortfolioMarginProAccountBalance operation in AccountApi.
 * @interface GetPortfolioMarginProAccountBalanceRequest
 */
export interface GetPortfolioMarginProAccountBalanceRequest {
    /**
     *
     * @type {string}
     * @memberof AccountApiGetPortfolioMarginProAccountBalance
     */
    readonly asset?: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetPortfolioMarginProAccountBalance
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getPortfolioMarginProAccountInfo operation in AccountApi.
 * @interface GetPortfolioMarginProAccountInfoRequest
 */
export interface GetPortfolioMarginProAccountInfoRequest {
    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetPortfolioMarginProAccountInfo
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getPortfolioMarginProSpanAccountInfo operation in AccountApi.
 * @interface GetPortfolioMarginProSpanAccountInfoRequest
 */
export interface GetPortfolioMarginProSpanAccountInfoRequest {
    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetPortfolioMarginProSpanAccountInfo
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for getTransferableEarnAssetBalanceForPortfolioMargin operation in AccountApi.
 * @interface GetTransferableEarnAssetBalanceForPortfolioMarginRequest
 */
export interface GetTransferableEarnAssetBalanceForPortfolioMarginRequest {
    /**
     * `LDUSDT` and `RWUSD`
     * @type {string}
     * @memberof AccountApiGetTransferableEarnAssetBalanceForPortfolioMargin
     */
    readonly asset: string;

    /**
     * `EARN_TO_FUTURE` /`FUTURE_TO_EARN`
     * @type {string}
     * @memberof AccountApiGetTransferableEarnAssetBalanceForPortfolioMargin
     */
    readonly transferType: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiGetTransferableEarnAssetBalanceForPortfolioMargin
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for portfolioMarginProBankruptcyLoanRepay operation in AccountApi.
 * @interface PortfolioMarginProBankruptcyLoanRepayRequest
 */
export interface PortfolioMarginProBankruptcyLoanRepayRequest {
    /**
     * SPOT or MARGIN，default SPOT
     * @type {string}
     * @memberof AccountApiPortfolioMarginProBankruptcyLoanRepay
     */
    readonly from?: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiPortfolioMarginProBankruptcyLoanRepay
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for queryPortfolioMarginProBankruptcyLoanAmount operation in AccountApi.
 * @interface QueryPortfolioMarginProBankruptcyLoanAmountRequest
 */
export interface QueryPortfolioMarginProBankruptcyLoanAmountRequest {
    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiQueryPortfolioMarginProBankruptcyLoanAmount
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for queryPortfolioMarginProBankruptcyLoanRepayHistory operation in AccountApi.
 * @interface QueryPortfolioMarginProBankruptcyLoanRepayHistoryRequest
 */
export interface QueryPortfolioMarginProBankruptcyLoanRepayHistoryRequest {
    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiQueryPortfolioMarginProBankruptcyLoanRepayHistory
     */
    readonly startTime?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiQueryPortfolioMarginProBankruptcyLoanRepayHistory
     */
    readonly endTime?: number | bigint;

    /**
     * Currently querying page. Start from 1. Default:1
     * @type {number | bigint}
     * @memberof AccountApiQueryPortfolioMarginProBankruptcyLoanRepayHistory
     */
    readonly current?: number | bigint;

    /**
     * Default:10 Max:100
     * @type {number | bigint}
     * @memberof AccountApiQueryPortfolioMarginProBankruptcyLoanRepayHistory
     */
    readonly size?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiQueryPortfolioMarginProBankruptcyLoanRepayHistory
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for queryPortfolioMarginProNegativeBalanceInterestHistory operation in AccountApi.
 * @interface QueryPortfolioMarginProNegativeBalanceInterestHistoryRequest
 */
export interface QueryPortfolioMarginProNegativeBalanceInterestHistoryRequest {
    /**
     *
     * @type {string}
     * @memberof AccountApiQueryPortfolioMarginProNegativeBalanceInterestHistory
     */
    readonly asset?: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiQueryPortfolioMarginProNegativeBalanceInterestHistory
     */
    readonly startTime?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiQueryPortfolioMarginProNegativeBalanceInterestHistory
     */
    readonly endTime?: number | bigint;

    /**
     * Default:10 Max:100
     * @type {number | bigint}
     * @memberof AccountApiQueryPortfolioMarginProNegativeBalanceInterestHistory
     */
    readonly size?: number | bigint;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiQueryPortfolioMarginProNegativeBalanceInterestHistory
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for repayFuturesNegativeBalance operation in AccountApi.
 * @interface RepayFuturesNegativeBalanceRequest
 */
export interface RepayFuturesNegativeBalanceRequest {
    /**
     * SPOT or MARGIN，default SPOT
     * @type {string}
     * @memberof AccountApiRepayFuturesNegativeBalance
     */
    readonly from?: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiRepayFuturesNegativeBalance
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for setMarginCallLevel operation in AccountApi.
 * @interface SetMarginCallLevelRequest
 */
export interface SetMarginCallLevelRequest {
    /**
     * The value of marginCallLevel must be within the range [1.1, 2.0].
     * @type {number}
     * @memberof AccountApiSetMarginCallLevel
     */
    readonly marginCallLevel: number;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiSetMarginCallLevel
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for switchDeltaMode operation in AccountApi.
 * @interface SwitchDeltaModeRequest
 */
export interface SwitchDeltaModeRequest {
    /**
     * `true` to enable Delta mode; `false` to disable Delta mode
     * @type {string}
     * @memberof AccountApiSwitchDeltaMode
     */
    readonly deltaEnabled: string;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiSwitchDeltaMode
     */
    readonly recvWindow?: number | bigint;
}

/**
 * Request parameters for transferLdusdtRwusdForPortfolioMargin operation in AccountApi.
 * @interface TransferLdusdtRwusdForPortfolioMarginRequest
 */
export interface TransferLdusdtRwusdForPortfolioMarginRequest {
    /**
     * `LDUSDT` and `RWUSD`
     * @type {string}
     * @memberof AccountApiTransferLdusdtRwusdForPortfolioMargin
     */
    readonly asset: string;

    /**
     * `EARN_TO_FUTURE` /`FUTURE_TO_EARN`
     * @type {string}
     * @memberof AccountApiTransferLdusdtRwusdForPortfolioMargin
     */
    readonly transferType: string;

    /**
     *
     * @type {number}
     * @memberof AccountApiTransferLdusdtRwusdForPortfolioMargin
     */
    readonly amount: number;

    /**
     *
     * @type {number | bigint}
     * @memberof AccountApiTransferLdusdtRwusdForPortfolioMargin
     */
    readonly recvWindow?: number | bigint;
}

/**
 * AccountApi - object-oriented interface
 * @class AccountApi
 */
export class AccountApi implements AccountApiInterface {
    private readonly configuration: ConfigurationRestAPI;
    private localVarAxiosParamCreator;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.localVarAxiosParamCreator = AccountApiAxiosParamCreator(configuration);
    }

    /**
     * BNB transfer can be between Margin Account and USDM Account
     *
     *
     * You can only use this function 2 times per 10 minutes in a rolling manner
     *
     * Weight: 1500
     *
     * @summary BNB transfer(USER_DATA)
     * @param {BnbTransferRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<BnbTransferResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/BNB-transfer Binance API Documentation}
     */
    public async bnbTransfer(
        requestParameters: BnbTransferRequest
    ): Promise<RestApiResponse<BnbTransferResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.bnbTransfer(
            requestParameters?.amount,
            requestParameters?.transferSide,
            requestParameters?.recvWindow
        );
        return sendRequest<BnbTransferResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Change Auto-repay-futures Status
     *
     * Weight: 1500
     *
     * @summary Change Auto-repay-futures Status(TRADE)
     * @param {ChangeAutoRepayFuturesStatusRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<ChangeAutoRepayFuturesStatusResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Change-Auto-repay-futures-Status Binance API Documentation}
     */
    public async changeAutoRepayFuturesStatus(
        requestParameters: ChangeAutoRepayFuturesStatusRequest
    ): Promise<RestApiResponse<ChangeAutoRepayFuturesStatusResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.changeAutoRepayFuturesStatus(
            requestParameters?.autoRepay,
            requestParameters?.recvWindow
        );
        return sendRequest<ChangeAutoRepayFuturesStatusResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Delete the margin call level for a Portfolio Margin account.
     *
     * Weight: 1500
     *
     * @summary Delete Margin Call Level (USER_DATA)
     * @param {DeleteMarginCallLevelRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<DeleteMarginCallLevelResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Delete-Margin-Call-Level Binance API Documentation}
     */
    public async deleteMarginCallLevel(
        requestParameters: DeleteMarginCallLevelRequest = {}
    ): Promise<RestApiResponse<DeleteMarginCallLevelResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.deleteMarginCallLevel(
            requestParameters?.recvWindow
        );
        return sendRequest<DeleteMarginCallLevelResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Transfers all assets from Futures Account to Margin account
     *
     * The BNB would not be collected from UM-PM account to the Portfolio Margin account.
     * You can only use this function 500 times per hour in a rolling manner.
     *
     * Weight: 1500
     *
     * @summary Fund Auto-collection(USER_DATA)
     * @param {FundAutoCollectionRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<FundAutoCollectionResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Fund-Auto-collection Binance API Documentation}
     */
    public async fundAutoCollection(
        requestParameters: FundAutoCollectionRequest = {}
    ): Promise<RestApiResponse<FundAutoCollectionResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.fundAutoCollection(
            requestParameters?.recvWindow
        );
        return sendRequest<FundAutoCollectionResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Transfers specific asset from Futures Account to Margin account
     *
     * The BNB transfer is not be supported
     *
     * Weight: 60
     *
     * @summary Fund Collection by Asset(USER_DATA)
     * @param {FundCollectionByAssetRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<FundCollectionByAssetResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Fund-Collection-by-Asset Binance API Documentation}
     */
    public async fundCollectionByAsset(
        requestParameters: FundCollectionByAssetRequest
    ): Promise<RestApiResponse<FundCollectionByAssetResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.fundCollectionByAsset(
            requestParameters?.asset,
            requestParameters?.recvWindow
        );
        return sendRequest<FundCollectionByAssetResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Query Auto-repay-futures Status
     *
     * Weight: 30
     *
     * @summary Get Auto-repay-futures Status(USER_DATA)
     * @param {GetAutoRepayFuturesStatusRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetAutoRepayFuturesStatusResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Get-Auto-repay-futures-Status Binance API Documentation}
     */
    public async getAutoRepayFuturesStatus(
        requestParameters: GetAutoRepayFuturesStatusRequest = {}
    ): Promise<RestApiResponse<GetAutoRepayFuturesStatusResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.getAutoRepayFuturesStatus(
            requestParameters?.recvWindow
        );
        return sendRequest<GetAutoRepayFuturesStatusResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Query the Delta mode status of current account.
     *
     * Weight: 1500
     *
     * @summary Get Delta Mode Status(USER_DATA)
     * @param {GetDeltaModeStatusRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetDeltaModeStatusResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Get-Delta-Mode-Status Binance API Documentation}
     */
    public async getDeltaModeStatus(
        requestParameters: GetDeltaModeStatusRequest = {}
    ): Promise<RestApiResponse<GetDeltaModeStatusResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.getDeltaModeStatus(
            requestParameters?.recvWindow
        );
        return sendRequest<GetDeltaModeStatusResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get the margin call level for a Portfolio Margin account.
     *
     * Weight: 1500
     *
     * @summary Get Margin Call Level (USER_DATA)
     * @param {GetMarginCallLevelRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetMarginCallLevelResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Get-Margin-Call-Level Binance API Documentation}
     */
    public async getMarginCallLevel(
        requestParameters: GetMarginCallLevelRequest = {}
    ): Promise<RestApiResponse<GetMarginCallLevelResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.getMarginCallLevel(
            requestParameters?.recvWindow
        );
        return sendRequest<GetMarginCallLevelResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Query Portfolio Margin Pro account balance
     *
     * Weight: 20
     *
     * @summary Get Portfolio Margin Pro Account Balance(USER_DATA)
     * @param {GetPortfolioMarginProAccountBalanceRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetPortfolioMarginProAccountBalanceResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Get-Classic-Portfolio-Margin-Balance-Info Binance API Documentation}
     */
    public async getPortfolioMarginProAccountBalance(
        requestParameters: GetPortfolioMarginProAccountBalanceRequest = {}
    ): Promise<RestApiResponse<GetPortfolioMarginProAccountBalanceResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.getPortfolioMarginProAccountBalance(
                requestParameters?.asset,
                requestParameters?.recvWindow
            );
        return sendRequest<GetPortfolioMarginProAccountBalanceResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get Portfolio Margin Pro Account Info
     *
     * Weight: 5
     *
     * @summary Get Portfolio Margin Pro Account Info(USER_DATA)
     * @param {GetPortfolioMarginProAccountInfoRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetPortfolioMarginProAccountInfoResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Get-Classic-Portfolio-Margin-Account-Info Binance API Documentation}
     */
    public async getPortfolioMarginProAccountInfo(
        requestParameters: GetPortfolioMarginProAccountInfoRequest = {}
    ): Promise<RestApiResponse<GetPortfolioMarginProAccountInfoResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.getPortfolioMarginProAccountInfo(
                requestParameters?.recvWindow
            );
        return sendRequest<GetPortfolioMarginProAccountInfoResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get Portfolio Margin Pro SPAN Account Info (For Portfolio Margin Pro SPAN users only)
     *
     * Weight: 5
     *
     * @summary Get Portfolio Margin Pro SPAN Account Info(USER_DATA)
     * @param {GetPortfolioMarginProSpanAccountInfoRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetPortfolioMarginProSpanAccountInfoResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Get-Classic-Portfolio-Margin-Account-Info-V2 Binance API Documentation}
     */
    public async getPortfolioMarginProSpanAccountInfo(
        requestParameters: GetPortfolioMarginProSpanAccountInfoRequest = {}
    ): Promise<RestApiResponse<GetPortfolioMarginProSpanAccountInfoResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.getPortfolioMarginProSpanAccountInfo(
                requestParameters?.recvWindow
            );
        return sendRequest<GetPortfolioMarginProSpanAccountInfoResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get transferable earn asset balance for all types of Portfolio Margin account
     *
     * Weight: 1500
     *
     * @summary Get Transferable Earn Asset Balance for Portfolio Margin (USER_DATA)
     * @param {GetTransferableEarnAssetBalanceForPortfolioMarginRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<GetTransferableEarnAssetBalanceForPortfolioMarginResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Get-Transferable-Earn-Asset-Balance-for-Portfolio-Margin Binance API Documentation}
     */
    public async getTransferableEarnAssetBalanceForPortfolioMargin(
        requestParameters: GetTransferableEarnAssetBalanceForPortfolioMarginRequest
    ): Promise<RestApiResponse<GetTransferableEarnAssetBalanceForPortfolioMarginResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.getTransferableEarnAssetBalanceForPortfolioMargin(
                requestParameters?.asset,
                requestParameters?.transferType,
                requestParameters?.recvWindow
            );
        return sendRequest<GetTransferableEarnAssetBalanceForPortfolioMarginResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Repay Portfolio Margin Pro Bankruptcy Loan
     *
     * Please note that the API Key has enabled Spot & Margin Trading permissions to access this endpoint.
     *
     * Weight: 3000
     *
     * @summary Portfolio Margin Pro Bankruptcy Loan Repay
     * @param {PortfolioMarginProBankruptcyLoanRepayRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<PortfolioMarginProBankruptcyLoanRepayResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Classic-Portfolio-Margin-Bankruptcy-Loan-Repay Binance API Documentation}
     */
    public async portfolioMarginProBankruptcyLoanRepay(
        requestParameters: PortfolioMarginProBankruptcyLoanRepayRequest = {}
    ): Promise<RestApiResponse<PortfolioMarginProBankruptcyLoanRepayResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.portfolioMarginProBankruptcyLoanRepay(
                requestParameters?.from,
                requestParameters?.recvWindow
            );
        return sendRequest<PortfolioMarginProBankruptcyLoanRepayResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Query Portfolio Margin Pro Bankruptcy Loan Amount
     *
     * If there’s no classic portfolio margin bankruptcy loan, the amount would be 0
     *
     * Weight: 500
     *
     * @summary Query Portfolio Margin Pro Bankruptcy Loan Amount(USER_DATA)
     * @param {QueryPortfolioMarginProBankruptcyLoanAmountRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<QueryPortfolioMarginProBankruptcyLoanAmountResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Query-Classic-Portfolio-Margin-Bankruptcy-Loan-Amount Binance API Documentation}
     */
    public async queryPortfolioMarginProBankruptcyLoanAmount(
        requestParameters: QueryPortfolioMarginProBankruptcyLoanAmountRequest = {}
    ): Promise<RestApiResponse<QueryPortfolioMarginProBankruptcyLoanAmountResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.queryPortfolioMarginProBankruptcyLoanAmount(
                requestParameters?.recvWindow
            );
        return sendRequest<QueryPortfolioMarginProBankruptcyLoanAmountResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Query repay history of pmloan for portfolio margin pro.
     *
     * `startTime` and `endTime` cannot be longer than 360 days
     * If `startTime` and `endTime` not sent, return records of the last 30 days by default.
     * If `startTime`is sent and `endTime` is not sent, return records of [startTime, startTime+30d].
     * If `startTime` is not sent and `endTime` is sent, return records of [endTime-30d, endTime].
     *
     * Weight: 500
     *
     * @summary Query Portfolio Margin Pro Bankruptcy Loan Repay History(USER_DATA)
     * @param {QueryPortfolioMarginProBankruptcyLoanRepayHistoryRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<QueryPortfolioMarginProBankruptcyLoanRepayHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Query-Portfolio-Margin-Pro-Bankruptcy-Loan-Repay-History Binance API Documentation}
     */
    public async queryPortfolioMarginProBankruptcyLoanRepayHistory(
        requestParameters: QueryPortfolioMarginProBankruptcyLoanRepayHistoryRequest = {}
    ): Promise<RestApiResponse<QueryPortfolioMarginProBankruptcyLoanRepayHistoryResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.queryPortfolioMarginProBankruptcyLoanRepayHistory(
                requestParameters?.startTime,
                requestParameters?.endTime,
                requestParameters?.current,
                requestParameters?.size,
                requestParameters?.recvWindow
            );
        return sendRequest<QueryPortfolioMarginProBankruptcyLoanRepayHistoryResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Query interest history of negative balance for portfolio margin.
     *
     * Weight: 50
     *
     * @summary Query Portfolio Margin Pro Negative Balance Interest History(USER_DATA)
     * @param {QueryPortfolioMarginProNegativeBalanceInterestHistoryRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<QueryPortfolioMarginProNegativeBalanceInterestHistoryResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Query-Classic-Portfolio-Margin-Negative-Balance-Interest-History Binance API Documentation}
     */
    public async queryPortfolioMarginProNegativeBalanceInterestHistory(
        requestParameters: QueryPortfolioMarginProNegativeBalanceInterestHistoryRequest = {}
    ): Promise<RestApiResponse<QueryPortfolioMarginProNegativeBalanceInterestHistoryResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.queryPortfolioMarginProNegativeBalanceInterestHistory(
                requestParameters?.asset,
                requestParameters?.startTime,
                requestParameters?.endTime,
                requestParameters?.size,
                requestParameters?.recvWindow
            );
        return sendRequest<QueryPortfolioMarginProNegativeBalanceInterestHistoryResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Repay futures Negative Balance
     *
     * Weight: 1500
     *
     * @summary Repay futures Negative Balance(USER_DATA)
     * @param {RepayFuturesNegativeBalanceRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<RepayFuturesNegativeBalanceResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Repay-futures-Negative-Balance Binance API Documentation}
     */
    public async repayFuturesNegativeBalance(
        requestParameters: RepayFuturesNegativeBalanceRequest = {}
    ): Promise<RestApiResponse<RepayFuturesNegativeBalanceResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.repayFuturesNegativeBalance(
            requestParameters?.from,
            requestParameters?.recvWindow
        );
        return sendRequest<RepayFuturesNegativeBalanceResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Set the margin call level for a Portfolio Margin account. When the account's uniMMR drops to the specified level, a notification will be sent via email and SMS.
     *
     * Weight: 1500
     *
     * @summary Set Margin Call Level (USER_DATA)
     * @param {SetMarginCallLevelRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<SetMarginCallLevelResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Set-Margin-Call-Level Binance API Documentation}
     */
    public async setMarginCallLevel(
        requestParameters: SetMarginCallLevelRequest
    ): Promise<RestApiResponse<SetMarginCallLevelResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.setMarginCallLevel(
            requestParameters?.marginCallLevel,
            requestParameters?.recvWindow
        );
        return sendRequest<SetMarginCallLevelResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Switch the Delta mode for existing PM PRO / PM RETAIL accounts.
     *
     * Weight: 1500
     *
     * @summary Switch Delta Mode(TRADE)
     * @param {SwitchDeltaModeRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<SwitchDeltaModeResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Switch-Delta-Mode Binance API Documentation}
     */
    public async switchDeltaMode(
        requestParameters: SwitchDeltaModeRequest
    ): Promise<RestApiResponse<SwitchDeltaModeResponse>> {
        const localVarAxiosArgs = await this.localVarAxiosParamCreator.switchDeltaMode(
            requestParameters?.deltaEnabled,
            requestParameters?.recvWindow
        );
        return sendRequest<SwitchDeltaModeResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Transfer LDUSDT/RWUSD as collateral for all types of Portfolio Margin account
     *
     * Weight: 1500
     *
     * @summary Transfer LDUSDT/RWUSD for Portfolio Margin(TRADE)
     * @param {TransferLdusdtRwusdForPortfolioMarginRequest} requestParameters Request parameters.
     * @returns {Promise<RestApiResponse<TransferLdusdtRwusdForPortfolioMarginResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @memberof AccountApi
     * @see {@link https://developers.binance.com/docs/derivatives/portfolio-margin-pro/account/Transfer-LDUSDT-Portfolio-Margin Binance API Documentation}
     */
    public async transferLdusdtRwusdForPortfolioMargin(
        requestParameters: TransferLdusdtRwusdForPortfolioMarginRequest
    ): Promise<RestApiResponse<TransferLdusdtRwusdForPortfolioMarginResponse>> {
        const localVarAxiosArgs =
            await this.localVarAxiosParamCreator.transferLdusdtRwusdForPortfolioMargin(
                requestParameters?.asset,
                requestParameters?.transferType,
                requestParameters?.amount,
                requestParameters?.recvWindow
            );
        return sendRequest<TransferLdusdtRwusdForPortfolioMarginResponse>(
            this.configuration,
            localVarAxiosArgs.endpoint,
            localVarAxiosArgs.method,
            localVarAxiosArgs.queryParams,
            localVarAxiosArgs.bodyParams,
            localVarAxiosArgs.headerParams,
            localVarAxiosArgs?.timeUnit,
            { isSigned: true }
        );
    }
}
